import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "./components/Sidebar.js";
import Header from "./components/Header.js";
import CommandPalette from "./components/CommandPalette.js";
import NavigationPopover from "./components/NavigationPopover.js";
import PagePlaceholder from "./components/PagePlaceholder.js";
import { SheetView } from "../../views/grid/index.js";
import { AdminCenter } from "../../views/admin/AdminCenter.js";
import { TimekeepingModule } from "../../modules/timekeeping";
import { DeleteFieldConfirmation } from "../DeleteFieldConfirmation.js";
import { applyTenantTheme, applyThemeModeBase } from "./theme/themeEngine.js";
import { DEFAULT_TENANT, TENANTS } from "./theme/tenants.js";
import { NAV_ITEMS } from "./constants.js";
import type { Tenant, ThemeMode, NavItem } from "./types.js";
import HeaderPopover, {
  type HeaderPopoverType,
} from "./components/HeaderPopover.js";
import { useAuth } from "../../contexts/AuthContext.js";
import { useQueryClient } from "@tanstack/react-query";

/**
 * State for field deletion confirmation dialog
 * Story 3.5: Implement Soft Delete Confirmation Dialog
 */
interface FieldDeleteConfirmState {
  isOpen: boolean;
  fieldId: string;
  fieldName: string;
  rowCount: number;
  resolve: ((confirmed: boolean) => void) | null;
}

// Storage key for persisting navigation path
const NAV_PATH_STORAGE_KEY = "swm-current-path";

const SwmShell: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Initialize from localStorage or default to 'sheets' (AC-6.2.2)
  const [currentPath, setCurrentPath] = useState(() => {
    const stored = localStorage.getItem(NAV_PATH_STORAGE_KEY);
    return stored || "sheets";
  });
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState("");
  const [navOverlaySectionId, setNavOverlaySectionId] = useState<string | null>(
    null
  );
  const [navOverlayAnchor, setNavOverlayAnchor] = useState<DOMRect | null>(
    null
  );
  const navOverlayCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [headerPopover, setHeaderPopover] = useState<{
    type: HeaderPopoverType;
    anchorRect: DOMRect;
  } | null>(null);
  const headerPopoverCloseTimeout = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);

  // Story 3.5: Field delete confirmation dialog state (AC-3.10)
  const [fieldDeleteConfirm, setFieldDeleteConfirm] =
    useState<FieldDeleteConfirmState>({
      isOpen: false,
      fieldId: "",
      fieldName: "",
      rowCount: 0,
      resolve: null,
    });

  // State for the current tenant (Theme)
  const [currentTenant, setCurrentTenant] = useState<Tenant>(DEFAULT_TENANT);
  const [availableWorkspaces, setAvailableWorkspaces] =
    useState<Tenant[]>(TENANTS);
  const [themeMode, setThemeMode] = useState<ThemeMode>("dark");

  // Fetch workspaces (domains) from tenant API
  useEffect(() => {
    async function fetchWorkspaces() {
      if (!user) return;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      // Manually construct X-Mock-User header since user object structure matches
      // what auth middleware expects (id, email, tenantId, role)
      headers["X-Mock-User"] = JSON.stringify(user);

      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/tenant`, { headers });
        if (res.ok) {
          const data = await res.json();
          // Map domains to Workspace (Tenant interface)
          const domains = data.domains || [];

          const workspaces: Tenant[] = domains.map((d: any) => ({
            id: d.id,
            name: d.name,
            logo: d.branding?.logo || "",
            primaryColor: d.branding?.primaryColor || "#3b82f6",
            secondaryColor: d.branding?.secondaryColor || "#6366f1",
          }));

          // If no domains, use the tenant itself
          if (workspaces.length === 0 && data.id) {
            workspaces.push({
              id: data.id,
              name: data.name,
              logo: data.branding?.logo || "",
              primaryColor: data.branding?.primaryColor || "#3b82f6",
              secondaryColor: data.branding?.secondaryColor,
            });
          }

          if (workspaces.length > 0) {
            setAvailableWorkspaces(workspaces);
            // Default to the first workspace if the current one is not in the list
            // Or if we are still on the "Default" mock tenant
            setCurrentTenant((prev) => {
              const exists = workspaces.find((w) => w.id === prev.id);
              if (exists && prev.id !== "default-tenant") return prev;

              // Try to restore from storage
              const storedId = localStorage.getItem("swm-current-workspace-id");
              const stored = storedId
                ? workspaces.find((w) => w.id === storedId)
                : null;

              return stored || workspaces[0];
            });
          }
        }
      } catch (e) {
        console.error("Failed to fetch workspaces", e);
      }
    }

    fetchWorkspaces();
  }, [user]);

  // Persist current workspace (domain)
  useEffect(() => {
    if (currentTenant?.id) {
      localStorage.setItem("swm-current-workspace-id", currentTenant.id);
      // Invalidate all queries to ensure data is refetched with new domain context
      queryClient.invalidateQueries();
    }
  }, [currentTenant, queryClient]);

  // Persist navigation path to localStorage
  useEffect(() => {
    localStorage.setItem(NAV_PATH_STORAGE_KEY, currentPath);
  }, [currentPath]);

  // Apply theme whenever tenant changes
  useEffect(() => {
    applyThemeModeBase(themeMode);
  }, [themeMode]);

  useEffect(() => {
    applyTenantTheme(currentTenant, themeMode);
  }, [currentTenant, themeMode]);

  // Global Command+K Listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsCommandOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  /**
   * Story 3.5: Field delete confirmation callback (AC-3.10)
   * Story 4.6: Now used by SheetView (formerly WorksheetView)
   * Called by SheetView when user attempts to delete a field with data.
   * Returns a Promise that resolves to true if user confirms, false if cancelled.
   */
  const handleConfirmFieldDelete = useCallback(
    (
      fieldId: string,
      fieldName: string,
      rowCount: number
    ): Promise<boolean> => {
      return new Promise((resolve) => {
        setFieldDeleteConfirm({
          isOpen: true,
          fieldId,
          fieldName,
          rowCount,
          resolve,
        });
      });
    },
    []
  );

  /**
   * Story 3.5: Handle dialog confirm (AC-3.37)
   */
  const handleFieldDeleteConfirm = useCallback(() => {
    if (fieldDeleteConfirm.resolve) {
      fieldDeleteConfirm.resolve(true);
    }
    setFieldDeleteConfirm({
      isOpen: false,
      fieldId: "",
      fieldName: "",
      rowCount: 0,
      resolve: null,
    });
  }, [fieldDeleteConfirm.resolve]);

  /**
   * Story 3.5: Handle dialog cancel (AC-3.36)
   */
  const handleFieldDeleteCancel = useCallback(() => {
    if (fieldDeleteConfirm.resolve) {
      fieldDeleteConfirm.resolve(false);
    }
    setFieldDeleteConfirm({
      isOpen: false,
      fieldId: "",
      fieldName: "",
      rowCount: 0,
      resolve: null,
    });
  }, [fieldDeleteConfirm.resolve]);

  // Determine which module layout to show based on path
  const renderContent = () => {
    // ui-lab case hidden for Epic 6 - uncomment when needed
    // if (currentPath === 'ui-lab') {
    //   return <ButtonShowcase />;
    // }

    if (currentPath === "sheets" || currentPath === "dashboard") {
      // Dashboard redirects to Sheets view (AC-6.2.3)
      // Story 4.6: SheetView with useSheetData integration
      // TODO: Get sheetId from route params or selected sheet in module navigation
      // Using seeded sample sheet ID for MVP - will be replaced with route-based selection
      // Story 3.5: Pass confirmation callback for field deletion (AC-3.10)
      const domainId = localStorage.getItem("swm-current-workspace-id");
      // If no domain selected, use root pattern
      const sheetId = domainId
        ? `timeentry-sheet-id-${domainId}`
        : "timeentry-sheet-id-marion-tenant-id";
      return (
        <SheetView
          sheetId={sheetId}
          onConfirmFieldDelete={handleConfirmFieldDelete}
        />
      );
    }

    // Story 6.3: Formula Demo sheet for testing HyperFormula integration
    if (currentPath === "formula-demo") {
      return (
        <SheetView
          sheetId="formula-demo-sheet-id"
          onConfirmFieldDelete={handleConfirmFieldDelete}
        />
      );
    }

    // Story 5.5: Timekeeping module with 3-tab interface
    if (currentPath === "timekeeping") {
      return <TimekeepingModule />;
    }

    if (currentPath === "admin-center") {
      return <AdminCenter />;
    }

    const navItem = NAV_ITEMS.find((item: NavItem) => item.id === currentPath);
    const title =
      navItem?.label ??
      currentPath.charAt(0).toUpperCase() + currentPath.slice(1);
    const peerModules = navItem
      ? NAV_ITEMS.filter(
          (item: NavItem) => item.category === navItem.category
        ).map((item: NavItem) => ({
          id: item.id,
          label: item.label,
          description: `Blueprint for ${item.label}`,
        }))
      : [];

    return (
      <PagePlaceholder
        title={title}
        body={`Design the ${title} experience here when you are ready. This canvas stays minimal so you can focus on structure.`}
        categoryLabel={navItem?.category}
        modules={peerModules}
      />
    );
  };

  const clearNavOverlayTimeout = () => {
    if (navOverlayCloseTimeout.current) {
      clearTimeout(navOverlayCloseTimeout.current);
      navOverlayCloseTimeout.current = null;
    }
  };

  const closeHeaderPopover = () => setHeaderPopover(null);
  const clearHeaderPopoverTimeout = () => {
    if (headerPopoverCloseTimeout.current) {
      clearTimeout(headerPopoverCloseTimeout.current);
      headerPopoverCloseTimeout.current = null;
    }
  };

  const scheduleHeaderPopoverClose = () => {
    clearHeaderPopoverTimeout();
    headerPopoverCloseTimeout.current = setTimeout(
      () => setHeaderPopover(null),
      120
    );
  };

  const closeNavOverlay = () => {
    clearNavOverlayTimeout();
    setNavOverlaySectionId(null);
    setNavOverlayAnchor(null);
  };

  const scheduleNavOverlayClose = () => {
    clearNavOverlayTimeout();
    navOverlayCloseTimeout.current = setTimeout(() => {
      setNavOverlaySectionId(null);
      setNavOverlayAnchor(null);
    }, 120);
  };

  const handleHelpHover = (anchorRect: DOMRect) => {
    closeHeaderPopover();
    clearHeaderPopoverTimeout();
    clearNavOverlayTimeout();
    setNavOverlaySectionId("help");
    setNavOverlayAnchor(anchorRect);
  };

  const handleHelpToggle = (anchorRect: DOMRect) => {
    closeHeaderPopover();
    clearNavOverlayTimeout();
    if (navOverlaySectionId === "help") {
      setNavOverlaySectionId(null);
      setNavOverlayAnchor(null);
      return;
    }
    setNavOverlaySectionId("help");
    setNavOverlayAnchor(anchorRect);
  };

  const toggleHeaderPopover = (
    type: HeaderPopoverType,
    anchorRect: DOMRect
  ) => {
    clearHeaderPopoverTimeout();
    if (navOverlaySectionId) closeNavOverlay();
    setHeaderPopover(
      (prev: { type: HeaderPopoverType; anchorRect: DOMRect } | null) => {
        if (prev && prev.type === type) return null;
        return { type, anchorRect };
      }
    );
  };

  const showHeaderPopover = (type: HeaderPopoverType, anchorRect: DOMRect) => {
    clearHeaderPopoverTimeout();
    if (navOverlaySectionId) closeNavOverlay();
    setHeaderPopover({ type, anchorRect });
  };

  return (
    <div className="h-screen overflow-hidden bg-background text-foreground font-sans selection:bg-primary/30">
      {/* Background ambient glow - dynamically colored by primary */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary/10 blur-[120px] transition-colors duration-1000" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px] transition-colors duration-1000" />
      </div>

      <Sidebar
        isCollapsed={isSidebarCollapsed}
        toggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        onSectionHover={(sectionId: string, rect: DOMRect) => {
          closeHeaderPopover();
          clearNavOverlayTimeout();
          setNavOverlaySectionId(sectionId);
          setNavOverlayAnchor(rect);
        }}
        onSectionLeave={() => {
          scheduleNavOverlayClose();
        }}
        activeNavSectionId={navOverlaySectionId}
        onToggleAssistant={() => setIsAssistantOpen((prev) => !prev)}
      />

      <Header
        isSidebarCollapsed={isSidebarCollapsed}
        onOpenCommandPalette={() => setIsCommandOpen(true)}
        currentTenant={currentTenant}
        onTenantChange={setCurrentTenant}
        availableTenants={availableWorkspaces}
        onTenantColorsChange={(
          colors: Partial<Pick<Tenant, "primaryColor" | "secondaryColor">>
        ) => setCurrentTenant((prev: Tenant) => ({ ...prev, ...colors }))}
        themeMode={themeMode}
        onToggleThemeMode={() =>
          setThemeMode((prev: ThemeMode) =>
            prev === "dark" ? "light" : "dark"
          )
        }
        onHelpHover={handleHelpHover}
        onHelpToggle={handleHelpToggle}
        onHelpLeave={scheduleNavOverlayClose}
        activeHeaderPopover={headerPopover?.type ?? null}
        onNotificationsHover={(rect: DOMRect) =>
          showHeaderPopover("notifications", rect)
        }
        onNotificationsToggle={(rect: DOMRect) =>
          toggleHeaderPopover("notifications", rect)
        }
        onCreateHover={(rect: DOMRect) => showHeaderPopover("create", rect)}
        onCreateToggle={(rect: DOMRect) => toggleHeaderPopover("create", rect)}
        onHeaderPopoverLeave={scheduleHeaderPopoverClose}
        commandQuery={commandQuery}
        onCommandQueryChange={setCommandQuery}
        isCommandOpen={isCommandOpen}
        onNavigate={(path: string) => setCurrentPath(path)}
      />

      <CommandPalette
        isOpen={isCommandOpen}
        onClose={() => setIsCommandOpen(false)}
        onNavigate={setCurrentPath}
        onTenantSelect={setCurrentTenant}
        query={commandQuery}
      />

      <NavigationPopover
        isOpen={Boolean(navOverlaySectionId)}
        sectionId={navOverlaySectionId}
        anchorRect={navOverlayAnchor}
        onClose={() => {
          closeNavOverlay();
        }}
        onNavigate={(id: string) => {
          setCurrentPath(id);
          closeNavOverlay();
        }}
        currentPath={currentPath}
        onToggleAssistant={() => {
          setIsAssistantOpen((prev) => !prev);
          closeNavOverlay();
        }}
        onPopoverEnter={clearNavOverlayTimeout}
        onPopoverLeave={scheduleNavOverlayClose}
      />

      {headerPopover && (
        <HeaderPopover
          type={headerPopover.type}
          anchorRect={headerPopover.anchorRect}
          onClose={() => {
            clearHeaderPopoverTimeout();
            setHeaderPopover(null);
          }}
        />
      )}

      {/* Story 3.5: Field delete confirmation dialog (AC-3.10) */}
      <DeleteFieldConfirmation
        isOpen={fieldDeleteConfirm.isOpen}
        fieldName={fieldDeleteConfirm.fieldName}
        rowCount={fieldDeleteConfirm.rowCount}
        onConfirm={handleFieldDeleteConfirm}
        onCancel={handleFieldDeleteCancel}
      />

      {/* Main Content Area */}
      <main
        className={`
          relative z-10 pt-14 h-screen transition-all duration-swm ease-out bg-background flex flex-col overflow-hidden
          ${isSidebarCollapsed ? "pl-[72px]" : "pl-[240px]"}
        `}
      >
        {/*
          Main "Floating" Shell
          - Full-bleed for sheets/grid views (no padding)
          - Standard padding for other views
        */}
        <div
          className={`w-full bg-card/70 backdrop-blur-sm flex-1 overflow-auto border-3 border-border border-r-0 rounded-tl-swm-shell rounded-bl-swm-shell ${
            currentPath === "sheets" ||
            currentPath === "dashboard" ||
            currentPath === "timekeeping" ||
            currentPath === "formula-demo" ||
            currentPath === "admin-center"
              ? "p-0"
              : "p-6"
          }`}
        >
          {renderContent()}
        </div>

        <footer className="px-6 py-4 text-xs text-muted-foreground flex flex-wrap items-center gap-4 justify-end text-right shrink-0">
          <div className="flex items-center gap-3 text-muted-foreground/80">
            <span>Changelog</span>
            <span className="w-1 h-1 rounded-full bg-border/70" />
            <span>Status</span>
          </div>
          <span>© {new Date().getFullYear()} SWM Engine</span>
        </footer>
      </main>
    </div>
  );
};

export default SwmShell;
